import React, { useCallback, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import axios from 'axios';
import { GenericPdfContent } from '../../../../utils/pdf/GenericPdfContent';
import { CushingV2PdfContent } from './CushingV2PdfContent';
import { createPdfZipBundle } from '../../../../utils/pdf/createPdfZipBundle';
import { createDownloadLink } from '../../../../utils/pdf/createDownloadLink';
import { useCaseStore, usePdfStore } from '../../../../store/hooks';
import { ISecondStageValuesCushingV2, TFile } from '../../../pages/user/caseDraft/stages/cushing-v2/secondStage/SecondStage.types';

// A file slot only counts as a real attachment when it has a non-empty link.
// Defense-in-depth against the backend's `[]`-instead-of-null contract (RR-603) —
// an empty/link-less object must never reach the ZIP bundler (it would download the
// image-host root as a 1 KB junk `file-.file`).
const hasRealFile = (file: TFile | null | undefined): file is TFile => !!file && !!file.link;

const collectAttachmentFiles = (secondStage: ISecondStageValuesCushingV2): TFile[] => {
  const files: TFile[] = [];
  if (hasRealFile(secondStage.otherMethodsTkFiles)) files.push(secondStage.otherMethodsTkFiles);
  if (hasRealFile(secondStage.otherMethodsSomatostatinFiles)) files.push(secondStage.otherMethodsSomatostatinFiles);
  if (hasRealFile(secondStage.otherMethods68PETFiles)) files.push(secondStage.otherMethods68PETFiles);
  if (hasRealFile(secondStage.otherMethods18PETFiles)) files.push(secondStage.otherMethods18PETFiles);
  if (secondStage.otherMethodsOtherActive && secondStage.otherMethodsOtherArray) {
    secondStage.otherMethodsOtherArray.forEach((item) => {
      if (hasRealFile(item.otherMethodFiles)) files.push(item.otherMethodFiles);
    });
  }
  return files;
};

const fetchFileBlob = async (fileUrl: string): Promise<Blob> => {
  // Backend may return full URLs with accidental double slashes (e.g. https://host.pl//media/xxx.jpg).
  // Collapse consecutive slashes in the path, preserving the :// in the protocol.
  const rawUrl = fileUrl.startsWith('http')
    ? fileUrl
    : `${process.env.REACT_APP_IMAGE_URL || ''}${fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`}`;
  const url = rawUrl.replace(/([^:])\/\/+/g, '$1/');
  const response = await axios.get(url, {
    responseType: 'blob',
    withCredentials: false,
  });
  return response.data;
};

export const CushingV2PdfOrchestrator: React.FC = observer(() => {
  const caseStore = useCaseStore();
  const pdfStore = usePdfStore();
  const pdfContentRef = useRef<{ generatePdf: () => Promise<Blob | void> }>(null);

  const { zeroStage, secondStage } = caseStore;

  // Charts render as regular Recharts SVGs (not PdfChart wrappers),
  // so we use hasCharts=false to skip chart polling and trigger PDF after 300ms delay.
  // html2canvas captures SVG charts directly with allowTaint.
  const hasCharts = false;

  const handleProgress = useCallback(
    (percent: number, label: string) => {
      pdfStore.setProgress(percent, label);
    },
    [pdfStore]
  );

  const handlePdfSuccess = useCallback(
    async (pdfBlob: Blob) => {
      const title = zeroStage?.title || 'cushing-v2-case';
      const safeTitle = title.replace(/[^a-zA-Z0-9ąćęłńóśźżĄĆĘŁŃÓŚŹŻ\s-]/g, '');
      const pdfFilename = `${safeTitle}-opis-przypadku.pdf`;

      const ss = secondStage as ISecondStageValuesCushingV2;
      const attachmentFiles = ss ? collectAttachmentFiles(ss) : [];

      if (attachmentFiles.length === 0) {
        createDownloadLink({ blob: pdfBlob, filename: pdfFilename });
        pdfStore.close();
        return;
      }

      pdfStore.setProgress(96, 'Pakowanie plików do ZIP...');

      const zipFiles = attachmentFiles.map((f) => ({
        id: f.link,
        filename_download: f.originalName || f.name,
        extension: f.type || undefined,
      }));

      await createPdfZipBundle({
        pdfBlob,
        pdfFilename,
        zipFilename: `${safeTitle}-opis-przypadku.zip`,
        files: zipFiles,
        fetchBlob: fetchFileBlob,
      });

      pdfStore.close();
    },
    [pdfStore, zeroStage, secondStage]
  );

  const handlePdfError = useCallback(
    (error: Error) => {
      console.error('PDF generation failed:', error);
      pdfStore.close();
    },
    [pdfStore]
  );

  if (!zeroStage) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        left: '-9999px',
        top: 0,
        width: '794px',
        // No padding here: html2canvas captures the inner [data-pdf-id] element, so the
        // wrapper's padding would NOT become a PDF margin and would only shrink the captured
        // element below the 794px capture width (white gap). Keep the wrapper exactly 794px
        // so the captured element matches the html2canvas width; page margins live on the
        // inner DescriptionContainer instead (RR-603).
        padding: 0,
        backgroundColor: '#ffffff',
        // Match the live view text colour (Section { color: black }); the previous '#222'
        // made PDF body text look lighter than the on-screen view (RR-603). Inherited into
        // the captured [data-pdf-id] subtree.
        color: '#000',
        pointerEvents: 'none',
        overflow: 'visible',
        height: 'auto',
      }}
    >
      <GenericPdfContent
        ref={pdfContentRef}
        hasCharts={hasCharts}
        onPdfGeneratedSuccess={handlePdfSuccess}
        onPdfError={handlePdfError}
        onProgress={handleProgress}
      >
        <CushingV2PdfContent />
      </GenericPdfContent>
    </div>
  );
});
